import styled from '@emotion/styled'
import React, { Children, forwardRef } from 'react'
import { variant } from 'styled-system'

import Box, { BoxProps } from './Box'

const buttonFontBase = {
  fontFamily: 'heading',
  fontSize: [17, 16, 21],
  lineHeight: '1em',
  letterSpacing: ['0.02em', '0.015em', '0.02em'],
  fontWeight: ['600', '600', '600'],
}

const leadingTrimStyle = {
  mt: buttonFontBase.fontSize.map((fontSize) => `-${fontSize * 0.25}px`),
  transform: buttonFontBase.fontSize.map(
    (fontSize) => `translateY(${fontSize * 0.25}px)`
  ),
}

const primaryButtonBase = {
  ...buttonFontBase,
  whiteSpace: 'nowrap',
  borderWidth: 1.5,
  borderStyle: 'solid',
  transition: 'all 0.2s',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '4px',
  display: 'inline-flex',
}

const secondaryButtonBase = {
  ...buttonFontBase,
  whiteSpace: 'nowrap',
  px: 3,
  height: 58,
  borderWidth: [1, 0.5],
  borderStyle: 'solid',
  transition: 'all 0.2s',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '4px',
  display: 'inline-flex',
}

const Button = styled(Box)`
  border: none;
  cursor: pointer;
  outline: none;
  ${variant({
    variants: {
      primaryBlueLightBg: {
        ...primaryButtonBase,
        px: 4,
        height: 60,
        bg: '#0058AA',
        color: '#fff',
        borderColor: 'transparent',
      },
      primaryBlue: {
        ...primaryButtonBase,
        px: 4,
        height: 60,
        bg: '#1199FA',
        color: '#fff',
        borderColor: 'transparent',
      },
      primaryDark: {
        ...primaryButtonBase,
        px: 4,
        height: 60,
        bg: 'primaryPaleWhite',
        color: 'secondaryDarkNavy',
        borderColor: 'transparent',
        '&:hover': {
          borderColor: 'primaryBrightBlue',
          bg: 'transparent',
          color: 'primaryPaleWhite',
        },
      },
      primaryLight: {
        ...primaryButtonBase,
        px: 4,
        height: 60,
        bg: 'secondaryDarkNavy',
        color: 'primaryPaleWhite',
        borderColor: 'transparent',
        '&:hover': {
          borderColor: 'secondaryDarkNavy',
          bg: 'transparent',
          color: 'secondaryDarkNavy',
        },
      },
      primarySmallDark: {
        ...primaryButtonBase,
        px: 3,
        height: 52,
        bg: 'primaryPaleWhite',
        color: 'secondaryDarkNavy',
        borderColor: 'transparent',
        '&:hover': {
          borderColor: 'primaryBrightBlue',
          bg: 'transparent',
          color: 'primaryPaleWhite',
        },
      },
      primarySmallLight: {
        ...primaryButtonBase,
        px: 3,
        height: 52,
        bg: 'secondaryDarkNavy',
        color: 'primaryPaleWhite',
        borderColor: 'transparent',
        '&:hover': {
          borderColor: 'secondaryDarkNavy',
          bg: 'transparent',
          color: 'secondaryDarkNavy',
        },
      },
      primaryExtraSmallDark: {
        ...primaryButtonBase,
        px: [1.5, 3, 3],
        height: [40, 42, 48],
        bg: 'primaryPaleWhite',
        color: 'secondaryDarkNavy',
        borderColor: 'transparent',
        '&:hover': {
          borderColor: 'primaryBrightBlue',
          bg: 'transparent',
          color: 'primaryPaleWhite',
        },
      },
      primaryExtraSmallLight: {
        ...primaryButtonBase,
        px: [1.5, 3, 3],
        height: [40, 42, 48],
        bg: 'secondaryDarkNavy',
        color: 'primaryPaleWhite',
        borderColor: 'transparent',
        '&:hover': {
          borderColor: 'secondaryDarkNavy',
          bg: 'transparent',
          color: 'secondaryDarkNavy',
        },
      },
      secondaryDark: {
        ...secondaryButtonBase,
        borderColor: 'primaryPaleWhite',
        bg: 'transparent',
        color: 'white',
        '&:hover': {
          bg: 'secondaryDarkButtonHoverBg',
        },
      },
      exchangeButton: {
        ...secondaryButtonBase,
        bg: 'transparent',
        borderColor: 'secondaryDarkNavy',
        color: 'secondaryDarkNavy',
      },
      secondaryLight: {
        ...secondaryButtonBase,
        borderColor: 'secondaryDarkNavy',
        bg: 'transparent',
        color: 'secondaryDarkNavy',
        '&:hover': {
          bg: 'secondaryLightButtonHoverBg',
        },
      },
      secondaryBlue: {
        ...secondaryButtonBase,
        borderColor: 'secondaryPartnerBlue',
        bg: 'transparent',
        color: 'secondaryPartnerBlue',
        '&:hover': {
          bg: 'secondaryLightButtonHoverBg',
        },
      },
      plain: {
        bg: 'transparent',
        p: 0,
        textAlign: 'left',
      },
      navMenu: {
        bg: 'transparent',
        px: [4, 8],
        textAlign: 'left',
      },
    },
  })}
`

interface ButtonProps extends BoxProps {
  variant?: string
}

const ForwardedButton = forwardRef<HTMLAllCollection, ButtonProps>(
  ({ children, ...props }, ref) => {
    return (
      <Button as="button" ref={ref} {...props}>
        {Children.map(children, (child) => {
          if (typeof child === 'string') {
            return <Box customCss={leadingTrimStyle}>{child}</Box>
          }

          return child
        })}
      </Button>
    )
  }
)

ForwardedButton.displayName = 'Button'

export default ForwardedButton
